import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { PanelRightClose, PanelRightOpen } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface EditorShellProps {
  /** Top chrome — usually <TopBar ... /> or <DocChrome ... />. */
  topChrome?: ReactNode;
  /** Optional left rail (primary nav, outline, etc.). */
  leftRail?: ReactNode;
  /** Optional vertical icon nav between the doc and the right rail. */
  miniRail?: ReactNode;
  /** Right rail (Tools / Comments). */
  rightRail?: ReactNode;
  /**
   * Optional chat panel, rendered after the right rail in the same horizontal
   * band so the two coexist. It owns its own width; the shell only gives it a
   * slot. A fixed overlay would cover the rail, so the panel lives in-flow here.
   */
  chatPanel?: ReactNode;
  /** The doc surface — typically <TipTapBlogEditor /> or any centered content. */
  children: ReactNode;
  className?: string;
}

const RIGHT_RAIL_COLLAPSED_KEY = 'ed-right-rail-collapsed';
// Kept in sync with SideMiniRail's private dispatch name.
const MINI_RAIL_JUMP_EVENT = 'ed-mini-rail-jump';

/**
 * EditorShell — full-bleed editor layout.
 *
 *   ┌─────────────────────────────────────────────────────┐
 *   │  topChrome (TopBar / DocChrome)                     │
 *   ├──────┬───────────────────────────┬──────┬───────────┤
 *   │ left │  doc surface (children)   │ mini │  right    │
 *   │ rail │  (scrolls)                │ rail │  rail     │
 *   └──────┴───────────────────────────┴──────┴───────────┘
 *
 * The 340px right rail (score / quality / outline / tools) is collapsible —
 * a toggle above the mini-rail hides it to give the doc more room, and the
 * choice persists. Clicking a mini-rail jump icon while collapsed re-opens it
 * so the icons still land on their section. The doc-surface area scrolls; its
 * container has `data-doc-scroll` so margin overlays can hook in.
 */
export function EditorShell({
  topChrome,
  leftRail,
  miniRail,
  rightRail,
  chatPanel,
  children,
  className,
}: EditorShellProps) {
  const [rightCollapsed, setRightCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(RIGHT_RAIL_COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });

  const setCollapsed = useCallback((next: boolean) => {
    setRightCollapsed(next);
    try {
      localStorage.setItem(RIGHT_RAIL_COLLAPSED_KEY, next ? '1' : '0');
    } catch {
      /* storage unavailable — non-fatal */
    }
  }, []);

  // A mini-rail jump targets a section inside the (possibly hidden) right rail,
  // so reveal it when one fires while collapsed.
  useEffect(() => {
    if (!rightRail) return;
    const onJump = () => setCollapsed(false);
    window.addEventListener(MINI_RAIL_JUMP_EVENT, onJump);
    return () => window.removeEventListener(MINI_RAIL_JUMP_EVENT, onJump);
  }, [rightRail, setCollapsed]);

  // The page scrolls (not the shell), so the row grows with the document. The
  // chat slot is pinned under the sticky top chrome and sized to the viewport
  // so its composer stays on screen; that needs the chrome's live height.
  const topChromeRef = useRef<HTMLDivElement>(null);
  const [topChromeH, setTopChromeH] = useState(0);
  useEffect(() => {
    const el = topChromeRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setTopChromeH(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, [topChrome]);

  return (
    <div
      className={cn(
        'flex flex-1 flex-col h-full min-h-0 bg-warm-bg text-foreground',
        className,
      )}
    >
      {topChrome && (
        // Frozen while the page scrolls. top-16 parks it directly under the
        // app header (h-16); z-20 keeps it over the doc but under that header
        // (z-10 in its own stacking context) and any overlay. Nothing else in
        // the shell is pinned — the doc surface and right rail scroll normally.
        <div
          ref={topChromeRef}
          className="sticky top-16 z-20 shrink-0 border-b border-border bg-card"
        >
          {topChrome}
        </div>
      )}
      <div className="flex flex-1 min-h-0">
        {leftRail && (
          <div className="shrink-0 border-r border-border bg-card">{leftRail}</div>
        )}
        <div
          className="flex-1 min-w-0 min-h-0 overflow-y-auto bg-warm-bg"
          data-doc-scroll
        >
          <div className="mx-auto max-w-[1100px] px-[40px] pt-10 pb-[120px]">
            {children}
          </div>
        </div>
        {(miniRail || rightRail) && (
          <div className="shrink-0 flex flex-col border-l border-border bg-card">
            {rightRail && (
              <button
                type="button"
                onClick={() => setCollapsed(!rightCollapsed)}
                title={rightCollapsed ? 'Expand panel' : 'Collapse panel'}
                aria-label={rightCollapsed ? 'Expand panel' : 'Collapse panel'}
                aria-expanded={!rightCollapsed}
                className="flex h-9 w-full items-center justify-center border-b border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {rightCollapsed ? (
                  <PanelRightOpen className="size-4" />
                ) : (
                  <PanelRightClose className="size-4" />
                )}
              </button>
            )}
            {miniRail}
          </div>
        )}
        {rightRail && (
          <div
            className={cn(
              'shrink-0 bg-warm-bg flex flex-col min-h-0 transition-[width] duration-200',
              rightCollapsed ? 'w-0 overflow-hidden' : 'w-[340px]',
            )}
            aria-hidden={rightCollapsed}
          >
            {rightRail}
          </div>
        )}
        {chatPanel && (
          <div
            className="sticky self-start shrink-0"
            data-chat-slot
            style={{
              top: `calc(4rem + ${topChromeH}px)`,
              height: `calc(100svh - 4rem - ${topChromeH}px)`,
            }}
          >
            {chatPanel}
          </div>
        )}
      </div>
    </div>
  );
}
